/**
 * reviewProtocol — the review window's data and messages.
 *
 * The review window (an Office dialog, or a full-pane overlay where dialogs
 * are not available) is a pure view of the task pane's review queue. The pane
 * owns every decision (``useProposalReviewFlow``); it sends the window a
 * serializable ``ReviewState`` whenever the queue moves, and the window sends
 * back what the writer clicked.
 */

import type { SectionIssue } from "../../api/client";
import { flagLine, snippetForProposal, titleFieldLabel } from "../components/ProposalReviewCard";
import type { ProposalQueueItem, ProposalReviewPhase } from "../hooks/useProposalReviewFlow";
import { isTitleFieldProposal } from "../hooks/useProposalReviewFlow";
import type { TableEditSnippet } from "../services/tableEdit";

export interface ReviewItem {
  id: string;
  sectionNumber: string;
  sectionTitle: string;
  /** ``text``: a paragraph or block; ``table``: cell edits; ``field``: a title-page value. */
  kind: "text" | "table" | "field";
  before: string;
  after: string;
  /** The title-page field a ``field`` change fills ("Study Status"). */
  fieldLabel?: string;
  table?: TableEditSnippet | null;
  /** The flags this change closes, in words. */
  fills: string[];
  /** The flags still open after it. */
  stillOpen: string[];
  /** Where a change not written by the model came from. */
  sourceNote?: string | null;
  revisionCount: number;
}

export interface ReviewState {
  items: ReviewItem[];
  activeId: string | null;
  phase: ProposalReviewPhase;
  error: string | null;
  progressLabel: string | null;
  /** True while "Approve all" is working through the queue. */
  approvingAll: boolean;
  /** False when this change cannot be revised (Revise is hidden). */
  canRevise?: boolean;
  /** True when the writer may edit a text change before approving it. */
  canEdit?: boolean;
  /** Section of the change just approved, while it can still be undone. */
  undoSection?: string | null;
  /** Changes that arrived since the writer last moved through the list. */
  newCount?: number;
}

export type ParentToWindow = { type: "state"; state: ReviewState };

export type WindowToParent =
  | { type: "ready" }
  | { type: "select"; id: string }
  /** ``content``: the writer's edit of the proposed text, approved in its place. */
  | { type: "approve"; id: string; content?: string }
  | { type: "reject"; id: string }
  | { type: "revise"; id: string; guidance: string }
  | { type: "approveAll" }
  | { type: "stopAll" }
  /** Undo the change just approved. */
  | { type: "undo" }
  | { type: "minimise" }
  | { type: "close" };

export const EMPTY_REVIEW_STATE: ReviewState = {
  items: [],
  activeId: null,
  phase: "idle",
  error: null,
  progressLabel: null,
  approvingAll: false,
};

export function toReviewItem(item: ProposalQueueItem, issuesById?: Readonly<Record<string, SectionIssue>>): ReviewItem {
  const { proposal, sectionNumber, sectionTitle } = item;
  const named = (ids: readonly string[]) =>
    ids.map((id) => issuesById?.[id]).filter((i): i is SectionIssue => Boolean(i));
  const fillsIssues = named(proposal.issue_ids);
  const fills = fillsIssues.length > 0 ? fillsIssues.map(flagLine) : [...proposal.marker_texts];
  const unfixed = proposal.unfixed_issue_ids ?? [];
  const unfixedIssues = named(unfixed);
  const stillOpen =
    unfixedIssues.length === unfixed.length
      ? unfixedIssues.map(flagLine)
      : unfixed.length > 0
        ? [`${unfixed.length} flag${unfixed.length === 1 ? "" : "s"}`]
        : [];
  const isField = isTitleFieldProposal(proposal);
  const table = isField ? null : snippetForProposal(proposal);
  return {
    id: proposal.id,
    sectionNumber,
    sectionTitle,
    kind: isField ? "field" : table ? "table" : "text",
    before: proposal.original_text ?? "",
    after: proposal.proposed_text ?? "",
    fieldLabel: isField ? titleFieldLabel(proposal.title_field_key ?? "") : undefined,
    table,
    fills,
    stillOpen,
    sourceNote: proposal.source_note ?? null,
    revisionCount: proposal.revision_count ?? 0,
  };
}

export function toReviewState(
  flow: {
    queue: ProposalQueueItem[];
    active: ProposalQueueItem | null;
    phase: ProposalReviewPhase;
    error: string | null;
    progressLabel: string | null;
    lastApplied?: { sectionNumber: string } | null;
    newCount?: number;
  },
  issuesById: Readonly<Record<string, SectionIssue>> | undefined,
  approvingAll: boolean,
): ReviewState {
  return {
    items: flow.queue.map((item) => toReviewItem(item, issuesById)),
    activeId: flow.active?.proposal.id ?? null,
    phase: flow.phase,
    error: flow.error,
    progressLabel: flow.progressLabel,
    approvingAll,
    canEdit: true,
    undoSection: flow.lastApplied?.sectionNumber ?? null,
    newCount: flow.newCount ?? 0,
  };
}

/** Parse a message from the other side; null when it is not one of ours. */
export function parseMessage<T extends { type: string }>(raw: unknown): T | null {
  try {
    const value = typeof raw === "string" ? JSON.parse(raw) : raw;
    return value && typeof value === "object" && typeof (value as { type?: unknown }).type === "string"
      ? (value as T)
      : null;
  } catch {
    return null;
  }
}
